import { supabase } from "./supabase";
import * as lager from "./dokumentlager";

/**
 * Logoen, lokalt.
 *
 * ========  HVORFOR DEN MAA LIGGE PAA DISK  ========
 *
 * Nettflata kan hente en signert lenke hver gang den tegner menyen. Appen
 * kan ikke: den brukes i en kjeller uten dekning, og lenka varer en time.
 * En logo som forsvinner naar nettet gjoer det, er verre enn ingen logo —
 * og rapport-PDF-en lages ofte nettopp da, ved skiftslutt, langt fra
 * dekning.
 *
 * Derfor lastes fila ned ÉN gang per sti og blir liggende i det samme
 * lageret som dokumentene. Stien baerer tiden (`logo-<ms>.png`, se
 * `apps/web/src/state/logo.ts`), saa en byttet logo er en ny sti og dermed
 * et nytt filnavn — vi trenger aldri spoerre «er denne gammel?».
 *
 * ========  DEN RYDDER ETTER SEG  ========
 *
 * Gamle logoer slettes naar en ny er hentet. De er smaa, men de ligger paa
 * en privat telefon, og alt som ligger der skal ha en grunn til aa vaere
 * der.
 */

const PREFIKS = "logo__";

/** Filnavnet i lageret. Stien er unik, saa den kan baere navnet selv. */
function navnFor(sti: string): string {
  return PREFIKS + sti.replace(/[^A-Za-z0-9._-]/g, "_");
}

/**
 * Henter logoen ned hvis den ikke alt ligger der.
 *
 * Kalles naar appen er paa nett. Feiler den, skjer ingenting: flata viser
 * Infoflyt-merket, som er en riktig tilstand og ikke en feiltilstand.
 */
export async function sikre(sti: string | null | undefined): Promise<void> {
  if (sti === null || sti === undefined || sti === "") return;
  if (supabase === null) return;

  const navn = navnFor(sti);
  if (await lager.lesFil(navn) !== null) return;

  const { data, error } = await supabase.storage
    .from("logoer").createSignedUrl(sti, 3600);
  if (error !== null || data === null) return;

  try {
    const svar = await fetch(data.signedUrl);
    if (!svar.ok) return;
    const blob = await svar.blob();
    if (blob.size === 0) return;
    await lager.lagre(navn, blob);
  } catch {
    /* Uten dekning. Proeves igjen ved neste oppslag. */
  }
}

/** Blob-URL til visning, eller null. */
export async function lenke(sti: string | null | undefined): Promise<string | null> {
  if (sti === null || sti === undefined || sti === "") return null;
  return lager.les(navnFor(sti));
}

/**
 * Fila selv, for PDF-en.
 *
 * jsPDF tar en data-URL, ikke en blob-URL — den leser bytesene synkront
 * naar siden settes, og en blob-URL maatte vaert hentet foerst.
 */
export async function dataUrl(
  sti: string | null | undefined,
): Promise<string | null> {
  if (sti === null || sti === undefined || sti === "") return null;
  const fil = await lager.lesFil(navnFor(sti));
  if (fil === null) return null;
  return new Promise((ferdig) => {
    const leser = new FileReader();
    leser.onload = () => ferdig(typeof leser.result === "string" ? leser.result : null);
    leser.onerror = () => ferdig(null);
    leser.readAsDataURL(fil);
  });
}

/**
 * Rydder vekk logoer ingen produksjon peker paa lenger.
 *
 * ALLE STIENE SOM GJELDER, ikke én. Foerste utgave ryddet inne i `sikre`
 * og beholdt bare den den nettopp hadde hentet — med to produksjoner slettet
 * den andre den foerste, og en frilanser med tre jobber hadde aldri mer enn
 * én logo paa disk.
 */
export async function rydd(stier: readonly (string | null | undefined)[]): Promise<void> {
  const beholdes = new Set(
    stier.filter((s): s is string => s !== null && s !== undefined && s !== "")
      .map(navnFor),
  );
  try {
    const rot = await navigator.storage.getDirectory();
    const mappe = await rot.getDirectoryHandle("dokumenter", { create: true });
    const doede: string[] = [];
    // @ts-expect-error -- `keys()` finnes paa FileSystemDirectoryHandle, men
    // ikke i lib.dom ennaa. Den er i bruk i dokumentlageret fra foer.
    for await (const navn of mappe.keys()) {
      const n = String(navn);
      if (n.startsWith(PREFIKS) && !beholdes.has(n)) doede.push(n);
    }
    for (const n of doede) await lager.slett(n);
  } catch {
    /* Lageret er utilgjengelig. Da er det ingenting aa rydde heller. */
  }
}
